import { getErrorMessage } from "@/core/services";
import { useAsyncList } from "@/ds";
import { listFinanceiroObras } from "@/modules/financeiro-obras/financeiro-obras.gateway";
import { DEFAULT_FINANCEIRO_OBRAS_SORT } from "@/modules/financeiro-obras/financeiro-obras.types";
import type {
  FinanceiroObraItem,
  FinanceiroObrasFilters,
  FinanceiroObrasSort,
} from "@/modules/financeiro-obras/financeiro-obras.types";
import { LISTING_SEARCH_DEBOUNCE_MS, useListingCards } from "@/shared/hooks";

export const FINANCEIRO_OBRAS_FORBIDDEN_MESSAGE =
  "Você não tem permissão para consultar as obras do Financeiro.";
export const FINANCEIRO_OBRAS_ERROR_MESSAGE = "Não foi possível carregar as obras.";

const FINANCEIRO_OBRAS_MESSAGES = {
  forbidden: FINANCEIRO_OBRAS_FORBIDDEN_MESSAGE,
  error: FINANCEIRO_OBRAS_ERROR_MESSAGE,
};

type SortField = FinanceiroObrasSort["field"];

export interface UseFinanceiroObrasListaParams {
  search: string;
  filters: FinanceiroObrasFilters;
  page: number;
  pageSize: number;
  sort?: FinanceiroObrasSort;
}

interface ListaParams {
  busca: string;
  filters: FinanceiroObrasFilters;
  page: number;
  pageSize: number;
  sort: FinanceiroObrasSort;
}

/** Visão Lista: paginação, ordenação e filtros no servidor. */
export function useFinanceiroObrasLista({
  search,
  filters,
  page,
  pageSize,
  sort = DEFAULT_FINANCEIRO_OBRAS_SORT,
}: UseFinanceiroObrasListaParams) {
  const busca = search.trim();

  return useAsyncList<ListaParams, FinanceiroObraItem>({
    fetcher: (params, signal) => listFinanceiroObras({ ...params, signal }),
    params: { busca, filters, page, pageSize, sort },
    debounceKey: busca,
    debounceMs: LISTING_SEARCH_DEBOUNCE_MS,
    forbiddenMessage: FINANCEIRO_OBRAS_FORBIDDEN_MESSAGE,
    errorMessage: FINANCEIRO_OBRAS_ERROR_MESSAGE,
    resolveErrorMessage: getErrorMessage,
  });
}

export interface UseFinanceiroObrasCardsParams {
  search: string;
  /** Os filtros de status não entram aqui: cada coluna consulta o seu próprio status. */
  filters: FinanceiroObrasFilters;
  sort?: FinanceiroObrasSort;
  /** Códigos dos status que viram colunas, na ordem de exibição. */
  columns: string[];
}

/**
 * Visão Cards: uma consulta por coluna de status (`statusFinanceiro=X&pageSize=10&page=N`);
 * "Mostrar mais obras" pede a próxima página só daquela coluna e anexa sem duplicar.
 */
export function useFinanceiroObrasCards({
  search,
  filters,
  sort = DEFAULT_FINANCEIRO_OBRAS_SORT,
  columns,
}: UseFinanceiroObrasCardsParams) {
  return useListingCards<FinanceiroObraItem, FinanceiroObrasFilters, SortField>({
    search,
    filters,
    sort,
    columns,
    fetchColumnPage: ({ column, busca, filters: criteria, page, pageSize, sort: order, signal }) =>
      listFinanceiroObras({
        busca,
        filters: { ...criteria, statusFinanceiro: [column] },
        page,
        pageSize,
        sort: order,
        signal,
      }),
    getItemId: (obra) => obra.id,
    messages: FINANCEIRO_OBRAS_MESSAGES,
  });
}
